<template>
  <SectionTemplate
    title="亲手试试 AI视频生成原来这么精准可控"
    sub-title="官方元素库"
    :desc="`传统AI视频生成像是在开盲盒？有言支持你精准调节每一个元素，所见即所得。\n现在就换个角色、切个镜头，试试真正掌控感满满的AI视频创作。`"
    class="official-element"
  >
    <div class="avatar-detail-container">
      <VideoTabs
        v-for="item in items"
        :tabs="item.tabs"
        :desc="item.desc"
        :sound="item.sound"
        :title="item.title"
        :muted="isMuted"
      />
    </div>
    <TrailBtn class="official-element-trail-btn" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import VideoTabs from '~/design/widgets/multi-tabs-video/index.vue';
import { getFeUrl } from '~/utils/url';
import TrailBtn from '../../components/trail-btn.vue';

const isMuted = inject<boolean>('isMuted') || true;
const getVideoCoverUrl = (videoUrl: string) => videoUrl.replace(/\.webm$/i, '.png');

const items = [
  {
    title: '一键切换 人物',
    desc: '数千款超写实3D数字人，想要亲和讲师还是商务主持，均可一键替换，还支持外观细节个性化调整，让每条视频都能有不一样的主角。',
    tabs: [
      {
        id: 'Libai',
        title: '李白',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/avatars/李白-1017.webm',
        ),
      },
      {
        id: ' Liuliu',
        title: '刘柳',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/avatars/刘柳-1017.webm',
        ),
      },
      {
        id: 'Rina',
        title: 'Rina',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/avatars/Rina-1017.webm',
        ),
      },
    ],
  },
  {
    title: '一键切换 场景',
    desc: '发布会大舞台、课堂小舞台、新闻演播室...近千款高质量3D场景随意切换。无需场地布景即可一键成片，氛围感直接拉满。',
    tabs: [
      {
        id: 'nature',
        title: '自然风光',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/scenes/nature.webm',
        ),
      },
      {
        id: 'cozy-room',
        title: '温馨室内',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/scenes/cozy-room.webm',
        ),
      },
      {
        id: 'press',
        title: '发布会现场',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/scenes/press.webm',
        ),
      },
    ],
  },
  {
    title: '一键切换 语言和音色',
    desc: '多语种、多音色、多声线任你切换，讲解要稳、带货要燃，不论内容风格如何变化，声音始终自然真实不跳戏。',
    sound: true,
    tabs: [
      {
        id: 'e-commerce',
        title: '电商一姐',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/tones/1.webm',
        ),
      },
      {
        id: 'cheerful-girl',
        title: '爽快女孩',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/tones/2.webm',
        ),
      },
      {
        id: 'intimate-blogger',
        title: '知心博主',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/tones/3.webm',
        ),
      },
      {
        id: 'professional-vocal',
        title: '专业女声',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/tones/4.webm',
        ),
      },
    ],
  },
  {
    title: '灵活编辑 素材与脚本',
    desc: '插入PPT、图片、视频等素材，数字人可精准匹配素材内容进行讲解，信息传递更清晰。脚本也可随时调整，灵活又高效。',
    tabs: [
      {
        id: 'PPT',
        title: 'PPT',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/edit/2.webm',
        ),
      },
      {
        id: 'picture',
        title: '图片',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/edit/3.webm',
        ),
      },
      {
        id: 'video',
        title: '视频',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/edit/4.webm',
        ),
      },
      {
        id: 'script',
        title: '脚本',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/edit/1.webm',
        ),
      },
    ],
  },
  {
    title: '一键添加 动作',
    desc: '关键信息不想被忽略？一键插入点头、手势、指向等动作，让重点更突出、讲解更顺畅。动作精准匹配语义，表达自然更有力。',
    tabs: [
      {
        id: 'welcome',
        title: '欢迎',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/actions/1.webm',
        ),
      },
      {
        id: 'fighting',
        title: '加油',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/actions/2.webm',
        ),
      },
      {
        id: 'like',
        title: '点赞',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/actions/3-like.webm',
        ),
      },
    ],
  },
  {
    title: '一键切换 表情',
    desc: '表达不够打动人？加个表情试试。有言支持开心、严肃、困惑等多种情绪的一键添加，配合语音语调与动作风格，让讲解更有感染力。',
    tabs: [
      {
        id: 'happy',
        title: '开心',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/expressions/1.webm',
        ),
      },
      {
        id: 'solemn',
        title: '严肃',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/expressions/2.webm',
        ),
      },
      {
        id: 'confused',
        title: '困惑',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/expressions/3.webm',
        ),
      },
    ],
  },
  {
    title: '一键切换 镜头',
    desc: '一个固定镜头拍到底？太单调了。有言支持远景、中景、特写、画中画一键切换，轻松呈现多机位效果。画面更聚焦，节奏更紧凑。',
    tabs: [
      {
        id: 'part',
        title: '特写',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/shots/1-1017.webm',
        ),
      },
      {
        id: 'surround',
        title: '中景',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/shots/2-1017.webm',
        ),
      },
      {
        id: 'overview',
        title: '全景',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/shots/3-1017.webm',
        ),
      },
    ],
  },
  {
    title: '一键添加 包装',
    desc: '好内容，也需要好包装。有言内置字幕样式、片头片尾、背景音乐、贴纸花字和Logo水印等高质量素材，轻松点选就能让视频更吸睛、更出片。',
    tabs: [
      {
        id: 'sound',
        sound: true,
        title: '音乐/音效',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/packages/1.webm',
        ),
      },
      {
        id: 'subtitle',
        title: '字幕样式',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/packages/2.webm',
        ),
      },
      {
        id: 'text',
        title: '文字模板',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/packages/3.webm',
        ),
      },
      {
        id: 'sticker',
        title: '动态贴纸',
        videoUrl: getFeUrl(
          '/youyan-platform/ai-avatar-video-generation/official-element/packages/4.webm',
        ),
      },
    ],
  },
].map(item => ({
  ...item,
  tabs: item.tabs.map(tab => ({
    ...tab,
    image: getVideoCoverUrl(tab.videoUrl),
  })),
}));
</script>

<style lang="scss" scoped src="./pc.scss"></style>
<style lang="scss" scoped src="./mobile.scss"></style>
